import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import {
  type ToastUpdate,
  bindDealItemsInProgressToastToCreatedItem,
  bindDealItemsInProgressToastToDatasource,
  clearDealItemsInProgressToast,
  registerDealItemsInProgressToast,
} from '@/lib/deal-items-toast';
import {
  DatasourceInstructionMode,
  DealItemCreateInput,
} from '@/lib/graphql/generated/graphql';
import { useAddWebsiteDataSource } from '@/lib/hooks/useAddWebsiteDataSource';
import { useCreateDealItem } from '@/lib/hooks/useCreateDealItem';
import { useErrorToastHandler } from '@/lib/hooks/useErrorToastHandler';
import { getVatLabelKey } from '@/lib/utils';
import { dealAtom } from '@/states/deal.state';
import { storeAtom } from '@/states/store.state';
import { useAtomValue } from 'jotai';
import { ArrowLeft } from 'lucide-react';

import { BaseImportViewProps } from './import-view-props';

interface ViewProductsManualProps extends BaseImportViewProps {
  dealId: string;
  isInternalUser: boolean;
  onCloseDialog?: () => void;
}

export const ViewProductsManual: React.FC<ViewProductsManualProps> = ({
  setImportView,
  dealId,
  isInternalUser,
  onCloseDialog,
}) => {
  const { t } = useTranslation();
  const store = useAtomValue(storeAtom);
  const vatLabel = t(getVatLabelKey(store));
  // Form state
  const [form, setForm] = useState<Partial<DealItemCreateInput>>({});
  const [color, setColor] = useState('');
  const [size, setSize] = useState('');

  // Tab states. The URL tab triggers web scraping and is internal-only, so
  // external users default to (and only see) the manual form.
  const [mainTab, setMainTab] = useState<'form' | 'url'>(
    isInternalUser ? 'url' : 'form'
  );
  const [formTab, setFormTab] = useState<
    'identification' | 'content' | 'pricing' | 'logistics'
  >('identification');
  const [urlInput, setUrlInput] = useState('');

  // Hooks
  const { createItem, loading } = useCreateDealItem(dealId);
  const deal = useAtomValue(dealAtom);
  const {
    addWebsiteDataSource,
    loading: loadingUrl,
    error: errorUrl,
    domainError,
    isDomainError: isDomainErrorUrl,
  } = useAddWebsiteDataSource();
  const { toast } = useToast();
  const urlToastTrackerIdRef = useRef<string | null>(null);

  const clearUrlProgressToast = () => {
    if (!urlToastTrackerIdRef.current) return;
    clearDealItemsInProgressToast({ trackerId: urlToastTrackerIdRef.current });
    urlToastTrackerIdRef.current = null;
  };

  useErrorToastHandler({
    domainError,
    isDomainError: isDomainErrorUrl,
    error: errorUrl ?? null,
    domainToast: {
      titleKey: 'general.error_scraping',
      defaultMessageKey: 'general.error_scraping_message',
      clearProgress: clearUrlProgressToast,
    },
    genericToast: {
      titleKey: 'general.error_creation',
      getDescription: () => t('general.error_creation_product'),
    },
  });

  const onChange = <K extends keyof DealItemCreateInput>(
    field: K,
    value: DealItemCreateInput[K]
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!form.name) {
      return alert(t('general.error_required_name'));
    }

    if (!form.gtin) {
      return alert(t('general.error_required_gtin'));
    }

    let createToastTrackerId: string | undefined;
    try {
      const options: Array<{ name: string; value: string }> = [];
      if (color.trim()) {
        options.push({ name: 'Couleur', value: color.trim() });
      }
      if (size.trim()) {
        options.push({ name: 'Taille', value: size.trim() });
      }

      const itemToCreate: DealItemCreateInput = {
        ...form,
        options: options.length > 0 ? options : undefined,
      } as DealItemCreateInput;

      const { update, dismiss } = toast({
        description: `⏳ ${t('general.creation_in_progress')}`,
        duration: Number.POSITIVE_INFINITY,
      });
      const toastTrackerId = registerDealItemsInProgressToast(
        update as ToastUpdate,
        dismiss,
        {
          kind: 'itemsCreated',
        }
      );
      createToastTrackerId = toastTrackerId;
      const result = await createItem(itemToCreate);

      if (!result.id) {
        clearDealItemsInProgressToast({ trackerId: toastTrackerId });
        toast({
          title: t('general.error_creation'),
          description: t('general.error_creation_product'),
          variant: 'destructive',
        });
        return;
      }

      bindDealItemsInProgressToastToCreatedItem(toastTrackerId, result.id);
      onCloseDialog?.();
    } catch {
      if (createToastTrackerId) {
        clearDealItemsInProgressToast({ trackerId: createToastTrackerId });
      }
      toast({
        title: t('general.error_creation'),
        description: t('general.error_creation_product'),
        variant: 'destructive',
      });
    }
  };

  const handleUrlSubmit = async () => {
    try {
      new URL(urlInput);
    } catch {
      alert(t('general.error_valid_url'));
      return;
    }
    if (!deal?.hubspotDealId) {
      alert(t('general.error_hubspot_id_invalid'));
      return;
    }
    const { update, dismiss } = toast({
      description: `⏳ ${t('general.import_url_in_progress')}`,
      duration: Number.POSITIVE_INFINITY,
    });
    const toastTrackerId = registerDealItemsInProgressToast(
      update as ToastUpdate,
      dismiss
    );
    urlToastTrackerIdRef.current = toastTrackerId;
    try {
      const result = await addWebsiteDataSource({
        dealId,
        hubspotDealId: deal.hubspotDealId,
        productUrl: urlInput,
        instructionMode: DatasourceInstructionMode.CreateOnly,
      });

      if (result.success) {
        bindDealItemsInProgressToastToDatasource(toastTrackerId, result.id);
        urlToastTrackerIdRef.current = null;
        // Success: the SSE `DEAL` signal resolves the toast when the import completes
        onCloseDialog?.();
      } else {
        clearUrlProgressToast();
      }
    } catch {
      clearUrlProgressToast();
    }
  };

  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-2">
          <button
            className="rounded-md p-1 hover:bg-muted"
            onClick={() => setImportView('products')}
            aria-label="Revenir aux produits"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <DialogTitle>
            {t(
              isInternalUser
                ? 'components.import_flow.products_manual.title'
                : 'components.import_flow.products_manual.title_manual'
            )}
          </DialogTitle>
        </div>
        <DialogDescription>
          {t(
            isInternalUser
              ? 'components.import_flow.products_manual.description'
              : 'components.import_flow.products_manual.description_manual'
          )}
        </DialogDescription>
      </DialogHeader>

      {/* Main Tabs: Méthode d'ajout */}
      <Tabs
        value={mainTab}
        onValueChange={(v) => setMainTab(v as 'form' | 'url')}
        className="mt-6 flex h-[500px] flex-col overflow-hidden"
      >
        {/* Method selection tabs: only for internal users (URL = web scraping) */}
        {isInternalUser && (
          <TabsList className="mb-2 grid grid-cols-2 border-b">
            <TabsTrigger value="url">
              {t('components.import_flow.products_manual.add_url')}
            </TabsTrigger>
            <TabsTrigger value="form" data-testid="add-manual-import-button">
              {t('components.import_flow.products_manual.add_manual')}
            </TabsTrigger>
          </TabsList>
        )}

        {/* Tab: Ajouter manuellement (original form with 4 tabs) */}
        <TabsContent
          value="form"
          className="overflow-hidden p-0"
          data-testid="add-manual-import-form"
        >
          <Tabs
            value={formTab}
            onValueChange={(v) =>
              setFormTab(
                v as 'identification' | 'content' | 'pricing' | 'logistics'
              )
            }
            className="flex h-full flex-col overflow-hidden"
          >
            <TabsList className="grid grid-cols-4 border-b">
              <TabsTrigger value="identification">
                {t(
                  'components.import_flow.products_manual.tabs.identification'
                )}
              </TabsTrigger>
              <TabsTrigger value="content">
                {t('components.import_flow.products_manual.tabs.content')}
              </TabsTrigger>
              <TabsTrigger value="pricing">
                {t('components.import_flow.products_manual.tabs.pricing')}
              </TabsTrigger>
              <TabsTrigger value="logistics">
                {t('components.import_flow.products_manual.tabs.logistics')}
              </TabsTrigger>
            </TabsList>
            {/* Identification */}
            <TabsContent
              value="identification"
              className="space-y-4 overflow-auto p-4"
            >
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">
                  {t('kpi.best_seller')}
                </label>
                <Switch
                  checked={Boolean(form.isBestSeller)}
                  onCheckedChange={(ch) => onChange('isBestSeller', ch)}
                />
              </div>
              <div className="space-y-2">
                <label
                  className="text-sm font-medium"
                  data-testid="product-name-label"
                >
                  {t('kpi.name')} *
                </label>
                <Input
                  className="h-10"
                  data-testid="product-name-input"
                  value={form.name ?? ''}
                  onChange={(e) => onChange('name', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('kpi.url')}</label>
                <Input
                  className="h-10"
                  value={form.sourceUrl ?? ''}
                  onChange={(e) => onChange('sourceUrl', e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label
                    className="text-sm font-medium"
                    data-testid="gtin-label"
                  >
                    {t('kpi.gtin')} *
                  </label>
                  <Input
                    className="h-10"
                    data-testid="gtin-input"
                    value={form.gtin ?? ''}
                    onChange={(e) => onChange('gtin', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t('kpi.sku')}</label>
                  <Input
                    className="h-10"
                    value={form.sku ?? ''}
                    onChange={(e) => onChange('sku', e.target.value)}
                  />
                </div>
              </div>
            </TabsContent>
            {/* Contenu */}
            <TabsContent
              value="content"
              className="space-y-4 overflow-auto p-4"
            >
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {t('kpi.description')}
                </label>
                <Input
                  className="h-10"
                  value={form.description ?? ''}
                  onChange={(e) => onChange('description', e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    {t('components.import_flow.products_manual.color')}
                  </label>
                  <Input
                    className="h-10"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    {t('components.import_flow.products_manual.size')}
                  </label>
                  <Input
                    className="h-10"
                    value={size}
                    onChange={(e) => setSize(e.target.value)}
                  />
                </div>
              </div>
            </TabsContent>
            {/* Tarifs */}
            <TabsContent
              value="pricing"
              className="space-y-4 overflow-auto p-4"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    {t('kpi.list_price')} ({vatLabel})
                  </label>
                  <Input
                    className="h-10"
                    type="number"
                    value={form.listPrice ?? ''}
                    onChange={(e) =>
                      onChange('listPrice', e.target.valueAsNumber)
                    }
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    {t('kpi.discounted_price')} ({vatLabel})
                  </label>
                  <Input
                    className="h-10"
                    type="number"
                    value={form.discountedPrice ?? ''}
                    onChange={(e) =>
                      onChange('discountedPrice', e.target.valueAsNumber)
                    }
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    {t('kpi.choose_discount')}
                  </label>
                  <Input
                    className="h-10"
                    type="number"
                    value={form.chooseDiscount ?? ''}
                    onChange={(e) =>
                      onChange('chooseDiscount', e.target.valueAsNumber)
                    }
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    {t('kpi.choose_price')} ({vatLabel})
                  </label>
                  <Input
                    className="h-10"
                    type="number"
                    value={form.choosePrice ?? ''}
                    onChange={(e) =>
                      onChange('choosePrice', e.target.valueAsNumber)
                    }
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    {t('kpi.eee_price')} ({vatLabel})
                  </label>
                  <Input
                    className="h-10"
                    type="number"
                    value={form.eeePrice ?? ''}
                    onChange={(e) =>
                      onChange('eeePrice', e.target.valueAsNumber)
                    }
                  />
                </div>
              </div>
            </TabsContent>
            {/* Logistique */}
            <TabsContent
              value="logistics"
              className="space-y-4 overflow-auto p-4"
            >
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {t('kpi.made_in')}
                </label>
                <Input
                  className="h-10"
                  value={form.madeIn ?? ''}
                  onChange={(e) => onChange('madeIn', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {t('kpi.shipping_price')} ({vatLabel})
                </label>
                <Input
                  className="h-10"
                  type="number"
                  value={form.shippingPrice ?? ''}
                  onChange={(e) =>
                    onChange('shippingPrice', e.target.valueAsNumber)
                  }
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('kpi.stock')}</label>
                <Input
                  className="h-10"
                  type="number"
                  value={form.stockQuantity ?? ''}
                  onChange={(e) =>
                    onChange('stockQuantity', e.target.valueAsNumber)
                  }
                />
              </div>
            </TabsContent>
          </Tabs>
        </TabsContent>
        {/* Tab: Ajouter via URL (web scraping, internal users only) */}
        {isInternalUser && (
          <TabsContent value="url" className="space-y-4 overflow-auto p-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('kpi.url_shopify')} *
              </label>
              <Input
                className="h-10"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="https://..."
              />
            </div>
          </TabsContent>
        )}
      </Tabs>

      <DialogFooter className="mt-auto flex justify-end gap-4 p-4">
        <Button variant="outline" onClick={() => setImportView('products')}>
          {t('general.previous')}
        </Button>
        {mainTab === 'url' ? (
          <Button onClick={handleUrlSubmit} disabled={loadingUrl}>
            {loadingUrl
              ? t('general.creation_in_progress')
              : t('general.creation_confirm')}
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={loading}
            data-testid="add-manual-import-submit-button"
          >
            {loading
              ? t('general.creation_in_progress')
              : t('general.creation_confirm')}
          </Button>
        )}
      </DialogFooter>
    </>
  );
};
